import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublishedBlogBySlug, type BlogDetail as BlogDetailType } from '../services/blogService';
import Navbar from './Navbar';
import Footer from './Footer';
import { FaCalendar, FaUser, FaArrowLeft, FaSpinner } from 'react-icons/fa';

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<BlogDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get brandId from environment or use a default
  const brandId = import.meta.env.VITE_BRAND_ID;

  useEffect(() => {
    if (slug) {
      fetchBlog();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBlog = async () => {
    if (!slug) return;
    
    try {
      setLoading(true);
      const data = await getPublishedBlogBySlug(brandId, slug);
      setBlog(data);
      setError(null);
    } catch (err) {
      setError('Blog post not found or failed to load.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-w-screen bg-[#141237ff] ">
      <Navbar />

      {/* Back Button Section */}
      <div className="w-full px-4 py-8 pt-24">
        <div className="max-w-[800px] mx-auto">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-[#FF2D55] hover:text-white transition-colors"
          >
            <FaArrowLeft />
            <span>Back to Blogs</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 pb-16 mobile:pb-12">
        <div className="max-w-[800px] mx-auto">
          {loading && (
            <div className="flex justify-center items-center py-20">
              <FaSpinner className="animate-spin text-[#FF2D55] text-4xl" />
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 text-center">
              <p className="text-red-400 text-lg">{error}</p>
              <Link
                to="/blogs"
                className="mt-4 inline-block px-6 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors"
              >
                Back to Blogs
              </Link>
            </div>
          )}

          {!loading && !error && blog && (
            <article className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
              {/* Featured Image */}
              {blog.featuredImage && (
                <div className="w-full h-[400px] overflow-hidden mobile:h-[250px]">
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-8 mobile:p-6">
                {/* Title */}
                <h1 className="text-white font-['Inter'] text-[36px] font-[700] leading-[44px] mb-4 mobile:text-[28px] mobile:leading-[36px]">
                  {blog.title}
                </h1>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-6 text-white/50 text-sm pb-6 mb-6 border-b border-white/10">
                  {blog.author && (
                    <div className="flex items-center gap-2">
                      <FaUser />
                      <span>{blog.author}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <FaCalendar />
                    <span>{formatDate(blog.publishedDate)}</span>
                  </div>
                </div>

                {/* Blog Content */}
                <div
                  className="prose prose-invert max-w-none text-white
                    prose-headings:text-white prose-headings:font-bold
                    prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-6
                    prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-5
                    prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4
                    prose-h4:text-lg prose-h4:mb-2 prose-h4:mt-3
                    prose-p:text-white/80 prose-p:leading-relaxed prose-p:my-4
                    prose-strong:text-white prose-strong:font-bold
                    prose-em:text-white/90
                    prose-ul:list-disc prose-ul:ml-6 prose-ul:my-4
                    prose-ol:list-decimal prose-ol:ml-6 prose-ol:my-4
                    prose-li:text-white/80 prose-li:my-1
                    prose-blockquote:border-l-4 prose-blockquote:border-pink-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-4 prose-blockquote:text-white/70
                    prose-code:bg-white/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:text-pink-300
                    prose-pre:bg-white/10 prose-pre:p-4 prose-pre:rounded prose-pre:overflow-x-auto prose-pre:my-4
                    prose-a:text-pink-400 prose-a:underline prose-a:cursor-pointer hover:prose-a:text-pink-300
                    prose-img:rounded prose-img:my-6 prose-img:shadow-lg
                  "
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </div>
            </article>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default BlogDetail;

